import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import getBatchValues from 'src/js/modules/element-settings/helpers/get-batch-values';
import ColumnsAlignmentsControl from '../components/columns-alignments-control';
import ColumnsAmountControl from '../components/columns-amount-control';
import controlIds from '../constants/control-ids';
import attributesConstants from '../constants/attributes';
import getColumnsControlSettings from '../settings/columns-control-settings';
import prepareInstructionsForColumnWidthReset from '../helpers/prepare-instructions-for-column-width-reset';
import createSubmitTraitsColumnAppearanceHandler from '../helpers/create-submit-traits-column-appearance-handler';

const { Divider } = designSystem;
const {
  Context: ElementSettingsContext,
  components: {
    Section,
    ColorSchemeControl,
    ActionRadioGroupControl,
    SelectControl,
    ToggleControl,
    ToggleableSectionControl,
    SliderControl,
  },
  constants: { stages },
  helpers: { createBatch, combineBatches, generateInitialState },
  hooks: { useMountChunk },
} = elementSettings;

const EVENT_TRAITS_PREFIX = 'columns';

const ColumnsAppearanceSettingsContainer = ({ tabId, elementId, settingId }) => {
  const chunkId = tabId;
  const { callbacks, onUpdateState, state } = useContext(ElementSettingsContext);

  const [noWrapValue] = getBatchValues(state, {
    elementIds: [elementId],
    attributes: [attributesConstants.NO_WRAP],
  }).flat();

  const controlSettings = useMemo(() => getColumnsControlSettings(elementId), [elementId]);

  const handleUpdateState = useCallback(
    (batch) => {
      onUpdateState(batch, { chunkId });
    },
    [chunkId, onUpdateState]
  );

  const handleChange = useCallback(
    ({ controlId, elementIds, attributes, value, skipTracking = false }) => {
      const batch = createBatch({ elementIds, attributes, value });

      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleChange = useCallback(
    ({ controlId, elementIds, attributes, value, skipTracking = false }) => {
      const batch = createBatch({ elementIds, attributes, value });

      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleableSectionChange = useCallback(
    ({ controlId, batch }) => {
      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: true });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleColumnsAmountChange = useCallback(
    ({ batch, controlId }) => {
      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleColumnWidthChange = useCallback(
    ({ controlId, elementIds, attributes, value }) => {
      const additionalBatch =
        value === 'fixed' ? null : prepareInstructionsForColumnWidthReset(elementIds);
      const mainBatch = createBatch({ elementIds, attributes, value });
      const batch = additionalBatch ? combineBatches([mainBatch, additionalBatch]) : mainBatch;

      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId });
    },
    [settingId, callbacks, handleUpdateState]
  );

  const traitsHandler = useMemo(
    () => createSubmitTraitsColumnAppearanceHandler({ prefix: EVENT_TRAITS_PREFIX }),
    []
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    initialState: generateInitialState(Object.values(controlSettings)),
    stageHandlers: [
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
  });

  if (!isChunkMounted) return null;

  return (
    <>
      <ColumnsAlignmentsControl elementId={elementId} onChange={handleChange} />
      <Divider appearance="light-grey" />
      <ColorSchemeControl {...controlSettings[controlIds.COLOR_SCHEME]} onChange={handleChange} />
      <Divider appearance="light-grey" />
      <Section title={i18next.t('Content alignment')}>
        <ActionRadioGroupControl
          {...controlSettings[controlIds.HORIZONTAL_CONTENT_ALIGNMENT]}
          nested
          onChange={handleChange}
        />
        <ActionRadioGroupControl
          {...controlSettings[controlIds.VERTICAL_CONTENT_ALIGNMENT]}
          nested
          onChange={handleChange}
        />
      </Section>
      <Divider appearance="light-grey" />
      <ColumnsAmountControl
        elementId={elementId}
        onChange={handleColumnsAmountChange}
        noWrapControl={controlSettings[controlIds.NO_WRAP]}
        onNoWrapChange={handleToggleChange}
      />
      <SelectControl
        {...controlSettings[controlIds.COLUMN_WIDTH]}
        onChange={handleColumnWidthChange}
      />
      <SelectControl {...controlSettings[controlIds.COLUMN_HEIGHT]} onChange={handleChange} />
      <Divider appearance="light-grey" />
      <Divider appearance="light-grey" />
      <ToggleableSectionControl
        {...controlSettings[controlIds.SPACING_SECTION]}
        onChange={handleToggleableSectionChange}
      >
        <SliderControl {...controlSettings[controlIds.VERTICAL_SPACING]} onChange={handleChange} />
        <SliderControl
          {...controlSettings[controlIds.HORIZONTAL_SPACING]}
          onChange={handleChange}
        />
        <Divider appearance="light-grey" />
        <SliderControl {...controlSettings[controlIds.HORIZONTAL_GAP]} onChange={handleChange} />
        {!noWrapValue && (
          <SliderControl {...controlSettings[controlIds.VERTICAL_GAP]} onChange={handleChange} />
        )}
      </ToggleableSectionControl>
      <Divider appearance="light-grey" />
      <ToggleableSectionControl
        {...controlSettings[controlIds.CORNERS_SECTION]}
        onChange={handleToggleableSectionChange}
      >
        <SliderControl {...controlSettings[controlIds.CORNERS]} onChange={handleChange} />
      </ToggleableSectionControl>
      <Divider appearance="light-grey" />
      <ToggleControl {...controlSettings[controlIds.BORDER]} onChange={handleToggleChange} />
      <Divider appearance="light-grey" />
      <ToggleControl {...controlSettings[controlIds.SHADOW]} onChange={handleToggleChange} />
    </>
  );
};

ColumnsAppearanceSettingsContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default ColumnsAppearanceSettingsContainer;
